//filtrar data de vehiculos por performanceStatus segun rango seleccionado por del usuario
import camelcaseKeys from 'camelcase-keys';
import moment from 'moment';
import { useMemo, useRef } from 'react';
import { isNotNullOrUndefined, isNullOrUndefined } from 'utility/Utils';

export const performanceColors = [
  '#FF4757',
  '#ffa437',
  '#2ED573',
  '#1E90FF',
  '#7d39f6',
  '#a90003',
  '#7B7B74',
  '#dcdcdc'
];

const vehiclePerformanceTitle = {
  Deficient: 'common.deficient',
  Regular: 'common.regular',
  Efficient: 'common.efficient',
  Top: 'common.top'
};

/**
 * Get translated title based on performance status
 * @param {number} status
 * @param {[number, number, number, number]} rangePerformance
 * @returns {string | null} Translated title
 */
export const getPerformanceTitle = (status, rangePerformance) => {
  const performanceStatus = Math.round(status);

  const isDeficient = performanceStatus <= rangePerformance[1] - 1;
  const isRegular =
    performanceStatus >= rangePerformance[1] && performanceStatus <= rangePerformance[2] - 1;
  const isEfficient =
    performanceStatus >= rangePerformance[2] && performanceStatus <= rangePerformance[3];
  const isTop = performanceStatus > rangePerformance[3] && performanceStatus < 200;

  let vehiclePerformance = null;

  if (isTop) {
    vehiclePerformance = vehiclePerformanceTitle.Top;
  } else if (isEfficient) {
    vehiclePerformance = vehiclePerformanceTitle.Efficient;
  } else if (isRegular) {
    vehiclePerformance = vehiclePerformanceTitle.Regular;
  } else if (isDeficient) {
    vehiclePerformance = vehiclePerformanceTitle.Deficient;
  }

  return vehiclePerformance;
};

/**
 * Get color based on performance status
 * @param {number} status
 * @param {number | undefined} expectedPerformance
 * @param {number[]} rangePerformance
 * @returns {string} Hex color
 */
export const getPerformanceColor = (
  status,
  expectedPerformance,
  rangePerformance = [0, 80, 90, 110, 200]
) => {
  // Ensure that rangePerformance has at least 5 elements
  if (rangePerformance.length < 5) {
    console.error('Invalid rangePerformance array: must have at least 5 elements.');
    return performanceColors[0]; // Default color if rangePerformance is invalid
  }

  const performanceStatus = Math.round(status);

  // Check the range and return the corresponding color
  if (!performanceStatus || performanceStatus < rangePerformance[1]) {
    return performanceColors[0];
  } else if (performanceStatus >= rangePerformance[1] && performanceStatus < rangePerformance[2]) {
    return performanceColors[1];
  } else if (performanceStatus >= rangePerformance[2] && performanceStatus < rangePerformance[3]) {
    return performanceColors[2];
  } else if (performanceStatus >= rangePerformance[3] && performanceStatus < rangePerformance[4]) {
    return performanceColors[3];
  } else {
    return performanceColors[4];
  }
};

const getAllFilteredData = (byVehicles, ranges = [0, 79, 80, 89, 90, 110]) => {
  const unitsWithRecentData = byVehicles.filter((v) => {
    // tiene que haber comunicado en los ultimos 7 dias
    const diff = moment().diff(v.systemDate, 'days');
    const hasRecentData = diff <= 7;

    // no debe tener downtime
    const hasNoDowntimeReasonId = isNullOrUndefined(v.downTimeReasonId);

    // tiene que tener un rendimiento que se pueda calcular
    // - rendimiento esperado > 0
    // AND (
    // - recorrido >= rendimiento esperado
    // - (ya no se aplica) recorrido >= rendimiento esperado * umbral de carga // tiene que recorrer la distancia para consumir la cantidad de combustible para detectar una carga
    // - consumo > 1 ? (litros/galones) //
    // ) OR rendimiento historico > 0
    const hasPerformance =
      (parseFloat(v.expectedfuelEconomyByVehicles) > 0 &&
        parseFloat(v.travel) >= parseFloat(v.expectedfuelEconomyByVehicles) &&
        //parseFloat(v.travel) >= (parseFloat(v.expectedfuelEconomyByVehicles) * parseFloat(v.loadThreshold)) &&
        parseFloat(v.consumption) > 1) ||
      v.historicalFuelEconomy > 0;

    return hasRecentData && hasNoDowntimeReasonId && hasPerformance;
  });

  const unitsWithoutPerformance = byVehicles.filter((v) => {
    const diff = moment().diff(v.systemDate, 'days');
    const hasRecentData = diff <= 7;
    const hasNoDowntimeReasonId = isNullOrUndefined(v.downTimeReasonId);
    const hasNoPerformance =
      parseFloat(v.expectedfuelEconomyByVehicles) <= 0 ||
      parseFloat(v.travel) < parseFloat(v.expectedfuelEconomyByVehicles) ||
      parseFloat(v.consumption) < 1;

    return (
      hasRecentData && hasNoDowntimeReasonId && hasNoPerformance && v.historicalFuelEconomy <= 0
    );
  });

  const dowtimeUnits = byVehicles.filter((u) => u.downTimeReasonId);

  const unitsWithoutRecentData = byVehicles.filter(
    (v) =>
      !unitsWithRecentData.some((v2) => v2.unitId === v.unitId) &&
      !unitsWithoutPerformance.some((v2) => v2.unitId === v.unitId) &&
      !dowtimeUnits.some((v2) => v2.unitId === v.unitId)
  );

  //0,80,90,110,200
  const allFilters = {
    unitsWithDischarges: filterTelemetryData(0.1, 0, unitsWithRecentData, 'unloaded'),
    unitsWithoutDischarges: filterTelemetryData(0, 0, unitsWithRecentData, 'unloaded'),
    poorPerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 0), // Deficiente
    regularPerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 1), // Regular
    goodPerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 2), // Eficiente
    overPerformance: unitsWithRecentData.filter(
      (v) => v.performanceStatusNew === 3 || v.performanceStatusNew === 4
    ), // Superior y Excesivo
    excessivePerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 4), // Excesivo
    unitsWithRecentData: unitsWithRecentData,
    unitsWithoutRecentData: unitsWithoutRecentData,
    dowtimeUnits: dowtimeUnits,
    unitsWithoutPerformance: unitsWithoutPerformance
  };

  return {
    ...allFilters,
    goodUnitsPercent: (allFilters.goodPerformance.length * 100) / unitsWithRecentData.length
  };
};

/* 
min: 0  max:0  condition: property=== "0.00"
min: 80 max:0  condition: property >= min
min: 10 max:50 condition: property <= max && property >= min
*/
const filterTelemetryData = (min = 0, max = 0, vehiclesData, property = 'performanceStatus') => {
  if (min === 0 && max === 0) {
    const filtered = vehiclesData.filter((v) => v[property] === '0.00');

    return filtered;
  } else {
    const filtered =
      max !== 0
        ? vehiclesData.filter((v) => v[property] <= max && v[property] >= min)
        : vehiclesData.filter((v) => v[property] >= min);
    return filtered;
  }
};

const Metrics = {
  ACCState: {
    MetricId: 1,
    MetricName: 'ACCState',
    BitNumber: 0,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 1
  },
  ACCX: {
    MetricId: 2,
    MetricName: 'ACCX',
    BitNumber: 1,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  ACCY: {
    MetricId: 3,
    MetricName: 'ACCY',
    BitNumber: 2,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  ACCZ: {
    MetricId: 4,
    MetricName: 'ACCZ',
    BitNumber: 3,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  AD1: {
    MetricId: 5,
    MetricName: 'AD1',
    BitNumber: 4,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  AD2: {
    MetricId: 6,
    MetricName: 'AD2',
    BitNumber: 5,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  AppVersion: {
    MetricId: 7,
    MetricName: 'AppVersion',
    BitNumber: 6,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 1,
    OrderNumber: 10
  },
  BackupBattery: {
    MetricId: 8,
    MetricName: 'BackupBattery',
    BitNumber: 7,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 5
  },
  Battery: {
    MetricId: 9,
    MetricName: 'Battery',
    BitNumber: 8,
    UseInVehicles: 0,
    UseInAssets: 1,
    UseInPeople: 1,
    OrderNumber: 2
  },
  CellNumber: {
    MetricId: 10,
    MetricName: 'CellNumber',
    BitNumber: 9,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 15
  },
  CoverTamperState: {
    MetricId: 11,
    MetricName: 'CoverTamperState',
    BitNumber: 10,
    UseInVehicles: 0,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 7
  },
  DoorState: {
    MetricId: 12,
    MetricName: 'DoorState',
    BitNumber: 11,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 6
  },
  FirmwareVersion: {
    MetricId: 13,
    MetricName: 'FirmwareVersion',
    BitNumber: 12,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  GSMSignalLevel: {
    MetricId: 14,
    MetricName: 'GSMSignalLevel',
    BitNumber: 13,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  HardwareVersion: {
    MetricId: 15,
    MetricName: 'HardwareVersion',
    BitNumber: 14,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  ICCID: {
    MetricId: 16,
    MetricName: 'ICCID',
    BitNumber: 15,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 18
  },
  IsCharging: {
    MetricId: 17,
    MetricName: 'IsCharging',
    BitNumber: 16,
    UseInVehicles: 0,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 3
  },
  MainVoltage: {
    MetricId: 18,
    MetricName: 'MainVoltage',
    BitNumber: 17,
    UseInVehicles: 1,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 4
  },
  MCC: {
    MetricId: 19,
    MetricName: 'MCC',
    BitNumber: 18,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 13
  },
  MNC: {
    MetricId: 20,
    MetricName: 'MNC',
    BitNumber: 19,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 14
  },
  OverSpeedState: {
    MetricId: 21,
    MetricName: 'OverSpeedState',
    BitNumber: 20,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  PadlockAuthorizationType: {
    MetricId: 22,
    MetricName: 'PadlockAuthorizationType',
    BitNumber: 21,
    UseInVehicles: 0,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 8
  },
  PadlockState: {
    MetricId: 23,
    MetricName: 'PadlockState',
    BitNumber: 22,
    UseInVehicles: 0,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 9
  },
  PLMN: {
    MetricId: 24,
    MetricName: 'PLMN',
    BitNumber: 23,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 12
  },
  Satellites: {
    MetricId: 25,
    MetricName: 'Satellites',
    BitNumber: 24,
    UseInVehicles: 1,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 11
  },
  ShockSensorState: {
    MetricId: 26,
    MetricName: 'ShockSensorState',
    BitNumber: 25,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 0
  },
  TrailerID: {
    MetricId: 27,
    MetricName: 'TrailerID',
    BitNumber: 26,
    UseInVehicles: 1,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 17
  },
  DriverId: {
    MetricId: 28,
    MetricName: 'DriverId',
    BitNumber: 27,
    UseInVehicles: 1,
    UseInAssets: 0,
    UseInPeople: 0,
    OrderNumber: 16
  },
  SolarVoltage: {
    MetricId: 29,
    MetricName: 'SolarVoltage',
    BitNumber: 28,
    UseInVehicles: 0,
    UseInAssets: 1,
    UseInPeople: 0,
    OrderNumber: 20
  },
  LocationAlways: {
    MetricId: 30,
    MetricName: 'LocationAlways',
    BitNumber: 29,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 1,
    OrderNumber: 21
  },
  LocationWhenInUse: {
    MetricId: 31,
    MetricName: 'LocationWhenInUse',
    BitNumber: 30,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 1,
    OrderNumber: 22
  },
  IgnoreBatteryOptimizations: {
    MetricId: 32,
    MetricName: 'IgnoreBatteryOptimizations',
    BitNumber: 31,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 1,
    OrderNumber: 23
  },
  ActivityRecognition: {
    MetricId: 33,
    MetricName: 'ActivityRecognition',
    BitNumber: 32,
    UseInVehicles: 0,
    UseInAssets: 0,
    UseInPeople: 1,
    OrderNumber: 24
  }
};

/**
 * Obtiene el número total de métricas definidas dinámicamente
 * @returns {number} Total de métricas en el objeto Metrics
 */
const getMetricsCount = () => {
  return Object.keys(Metrics).length;
};

/**
 * Convierte un número decimal a binario con zero padding basado en el total de métricas
 * Maneja números negativos usando Two's Complement para UseInPeople
 * La cadena binaria retornada está INVERTIDA para que BitNumber 0 esté al principio
 * y BitNumber N esté al final, haciendo que el índice coincida con el BitNumber
 * @param {number} dec - Número decimal a convertir (puede ser negativo)
 * @returns {string} Representación binaria INVERTIDA con padding (BitNumber 0 al principio)
 */
const dec2bin = (dec) => {
  const metricsCount = getMetricsCount();
  let binary = '';
  // Manejar números negativos con Two's Complement
  if (dec < 0) {
    // Convertir a número sin signo usando Two's Complement
    // Para N bits (metricsCount): 2^metricCount + dec (donde dec es negativo)
    // Para N bits: 2^N + dec (donde dec es negativo)  
    const maxValue = BigInt(2) ** BigInt(metricsCount);  
    dec = Number(maxValue + BigInt(dec)); // Esto da el valor positivo equivalente en Two's Complement  

  }

  // Conversión decimal a binario
  if (dec === 0) {
    binary = '0';
  } else {
    let tempDec = dec;
    while (tempDec > 0) {
      binary = (tempDec % 2) + binary;
      tempDec = Math.floor(tempDec / 2);
    }
  }

  // Zero padding a la izquierda para completar el total de métricas
  while (binary.length < metricsCount) {
    binary = '0' + binary;
  }

  // INVERTIR la cadena para que BitNumber 0 esté al principio (índice 0)
  // Esto hace que binary[bitNumber] coincida directamente con el bit correspondiente
  return reverseBinary(binary);
};

const reverseBinary = (binary) => {
  let reversedBinary = '';
  for (let i = binary.length - 1; i >= 0; i--) {
    reversedBinary += binary[i];
  }
  return reversedBinary;
};

/**
 * Convierte un número decimal a binario invertido con zero padding.
 * Alias de dec2bin para mantener compatibilidad con código existente.
 * @param {number} dec - Número decimal a convertir (puede ser negativo)
 * @returns {string} Cadena binaria invertida con padding donde el índice coincide con BitNumber
 */
const dec2binReverse = (dec) => {
  return dec2bin(dec);
};

const searchUseMetric = (metricsMask, bitNumber) => {
  // Validar que metricsMask no sea null o undefined
  if (metricsMask == null) {
    return false;
  }

  // Convertir a binario (ya está invertido, así que bitNumber coincide con el índice)
  const binary = dec2bin(metricsMask);

  // Validar que bitNumber esté dentro de los límites de la cadena binaria
  if (bitNumber < 0 || bitNumber >= binary.length) {
    return false;
  }
  // Acceder directamente al bit usando bitNumber como índice
  // Gracias a que dec2bin invierte la cadena, binary[bitNumber] es el bit correcto
  return binary[bitNumber] === '1';
};

const getSummaryDataForDynamicTable = (performanceSummary, intl) => {
  if (performanceSummary?.length > 0) {
    const statuses = ['efficient', 'top', 'regular', 'deficient'];

    // Initialize an object to store the results
    const result = {};

    // Iterate through the data
    performanceSummary?.forEach((row) => {
      const weekNumber = row.weekNumber;
      // Iterate through the statuses
      statuses.forEach((status) => {
        const statusLowerCase = status.toLowerCase();
        // Create or update the status object
        if (!result[statusLowerCase]) {
          result[statusLowerCase] = {};
        }
        // Creamos un nuevo campo con el total del estado en la semana de la iteracion
        result[statusLowerCase][`totalWeek${weekNumber}`] = `${row[statusLowerCase]} - ${(
          (row[statusLowerCase] / row.total) *
          100
        ).toFixed(0)} %`;
      });
    });

    // Convert the result object to an array of objects
    const newConsolidatedInfo = Object.keys(result).map((status) => ({
      status: intl.formatMessage({ id: `common.${status}` }),
      ...result[status]
    }));

    return newConsolidatedInfo;
  }

  return [];
};

function useCombinedFuelHistory(data, samples, fuelEvents, sensors, selectedSensor) {
  // 🚨 THROTTLED LOGGING usando useRef
  const lastLogTimeRef = useRef(0);

  // 🚨 ESTABILIZAR dependencias usando solo primitivos para evitar re-renders
  const stableDependencies = useMemo(
    () => ({
      dataLength: data?.fuelHistory?.length ?? 0,
      samplesLength: samples?.fuelSamples?.length ?? 0,
      eventsLength: fuelEvents?.length ?? 0,
      sensorsLength: sensors?.length ?? 0,
      selectedSensorId: selectedSensor?.sensorId ?? -1,
      selectedSensorIsCombined: selectedSensor?.isCombined ?? false
    }),
    [
      data?.fuelHistory?.length,
      samples?.fuelSamples?.length,
      fuelEvents?.length,
      sensors?.length,
      selectedSensor?.sensorId,
      selectedSensor?.isCombined
    ]
  );

  const combinedData = useMemo(() => {
    // 🛑 EARLY EXIT para evitar procesamiento innecesario
    if (!data?.fuelHistory?.length) {
      console.log('🔍 useCombinedFuelHistory: No data, returning empty array');
      return [];
    }

    const result = data.fuelHistory.map((result) => {
      const resultFinal = camelcaseKeys(result);
      const match = fuelEvents?.find?.(
        (event) => event.unitPositionLogId === resultFinal.unitPositionLogId
      );

      // Permitir datos de tanques individuales
      const isSensorValid =
        stableDependencies.sensorsLength === 1 || // Unidad con un solo sensor
        stableDependencies.selectedSensorIsCombined || // Vista combinada artificial
        (!stableDependencies.selectedSensorIsCombined && stableDependencies.sensorsLength > 1); // Tanque individual en multi-sensor

      if (isSensorValid) {
        const sampleMatch = samples?.fuelSamples?.find?.(
          (sample) =>
            sample.unitPositionLogId === resultFinal.unitPositionLogId &&
            resultFinal.eventId !== 332 &&
            resultFinal.eventId !== 331
        );
        return {
          ...resultFinal,
          ...(match || sampleMatch)
        };
      }

      return resultFinal;
    });

    // 🐛 DEBUG: Solo loguear cuando realmente cambie el resultado
    const debugData = {
      originalDataLength: stableDependencies.dataLength,
      resultLength: result?.length ?? 0,
      hasData: result?.length > 0,
      samplesLength: stableDependencies.samplesLength,
      eventsLength: stableDependencies.eventsLength,
      sensorsLength: stableDependencies.sensorsLength,
      selectedSensorIsCombined: stableDependencies.selectedSensorIsCombined
    };

    // 🚨 THROTTLED LOGGING: Solo loguear cada 2000ms máximo
    const now = Date.now();
    if (!lastLogTimeRef.current || now - lastLogTimeRef.current > 2000) {
      console.log('🔍 useCombinedFuelHistory result (throttled):', debugData);
      lastLogTimeRef.current = now;
    }

    return result;
  }, [
    // 🚨 DEPENDENCIAS ESTABLES: Solo primitivos que realmente importan
    stableDependencies.dataLength,
    stableDependencies.samplesLength,
    stableDependencies.eventsLength,
    stableDependencies.sensorsLength,
    stableDependencies.selectedSensorId,
    stableDependencies.selectedSensorIsCombined,
    data?.fuelHistory, // Solo incluir si es necesario
    samples?.fuelSamples, // Solo incluir si es necesario
    fuelEvents // Solo incluir si es necesario
  ]);

  return combinedData;
}

// Weekly-specific filtering function for historical data
export const getWeeklyFilteredData = (byVehicles, dateRange) => {
  console.log('Weekly byVehicles:', byVehicles);

  const endDate = moment(dateRange[1]).isAfter(moment()) ? moment() : moment(dateRange[1]);

  console.log('Weekly endDate:', endDate);

  const unitsWithRecentData = byVehicles.filter((v) => {
    // For weekly data, include units with valid performance status
    //
    const diff = endDate.diff(v.systemDate, 'days');
    const hasRecentData = diff <= 7;

    if (v.unitId === 1405) {
      console.log(
        'Weekly unitId 1405 hasRecentData:',
        hasRecentData,
        'systemDate:',
        v.systemDate,
        'diff:',
        diff
      );
    }

    // no debe tener downtime
    const hasNoDowntimeReasonId = isNullOrUndefined(v.downTimeReasonId) || v.downTimeReasonId === 0;

    // tiene que tener un rendimiento que se pueda calcular
    // - rendimiento esperado > 0
    // AND (
    // - recorrido >= rendimiento esperado
    // - (ya no se aplica) recorrido >= rendimiento esperado * umbral de carga // tiene que recorrer la distancia para consumir la cantidad de combustible para detectar una carga
    // - consumo > 1 ? (litros/galones) //
    // ) OR rendimiento historico > 0
    const hasPerformance =
      (parseFloat(v.expectedfuelEconomyByVehicles) > 0 &&
        parseFloat(v.travel) >= parseFloat(v.expectedfuelEconomyByVehicles) &&
        //parseFloat(v.travel) >= (parseFloat(v.expectedfuelEconomyByVehicles) * parseFloat(v.loadThreshold)) &&
        parseFloat(v.consumption) > 1) ||
      v.historicalFuelEconomy > 0;

    return hasRecentData && hasNoDowntimeReasonId && hasPerformance;
  });

  console.log('Weekly unitsWithRecentData ---- :', unitsWithRecentData);

  const unitsWithoutPerformance = byVehicles.filter((v) => {
    const diff = endDate.diff(v.systemDate, 'days');
    const hasRecentData = diff <= 7;
    const hasNoDowntimeReasonId = isNullOrUndefined(v.downTimeReasonId) || v.downTimeReasonId === 0;
    const hasNoPerformance =
      parseFloat(v.expectedfuelEconomyByVehicles) <= 0 ||
      parseFloat(v.travel) < parseFloat(v.expectedfuelEconomyByVehicles) ||
      parseFloat(v.consumption) < 1;

    return (
      hasRecentData && hasNoDowntimeReasonId && hasNoPerformance && v.historicalFuelEconomy <= 0
    );
  });

  const dowtimeUnits = byVehicles.filter(
    (u) => isNotNullOrUndefined(u.downTimeReasonId) && u.downTimeReasonId !== 0
  );

  const unitsWithoutRecentData = byVehicles.filter(
    (v) =>
      !unitsWithRecentData.some((v2) => v2.unitId === v.unitId) &&
      !unitsWithoutPerformance.some((v2) => v2.unitId === v.unitId) &&
      !dowtimeUnits.some((v2) => v2.unitId === v.unitId)
  );

  // Calculate discharge information - be more flexible with data
  const unitsWithDischarges = unitsWithRecentData.filter((unit) => (unit.dischargeCount ?? 0) > 0);
  const unitsWithoutDischarges = unitsWithRecentData.filter(
    (unit) => (unit.dischargeCount ?? 0) === 0
  );

  console.log('Weekly filtering results:', {
    total: byVehicles.length,
    unitsWithRecentData: unitsWithRecentData.length,
    unitsWithoutRecentData: unitsWithoutRecentData.length,
    dowtimeUnits: dowtimeUnits.length,
    unitsWithoutPerformance: unitsWithoutPerformance.length,
    unitsWithDischarges: unitsWithDischarges.length,
    unitsWithoutDischarges: unitsWithoutDischarges.length
  });

  const allFilters = {
    unitsWithDischarges: unitsWithDischarges,
    unitsWithoutDischarges: unitsWithoutDischarges,
    poorPerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 0), // Deficiente
    regularPerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 1), // Regular
    goodPerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 2), // Eficiente
    overPerformance: unitsWithRecentData.filter(
      (v) => v.performanceStatusNew === 3 || v.performanceStatusNew === 4
    ), // Superior y Excesivo
    excessivePerformance: unitsWithRecentData.filter((v) => v.performanceStatusNew === 4), // Excesivo
    unitsWithRecentData: unitsWithRecentData,
    unitsWithoutRecentData: unitsWithoutRecentData,
    dowtimeUnits: dowtimeUnits,
    unitsWithoutPerformance: unitsWithoutPerformance
  };

  console.log('Performance breakdown:', {
    poorPerformance: allFilters.poorPerformance.length,
    regularPerformance: allFilters.regularPerformance.length,
    goodPerformance: allFilters.goodPerformance.length,
    overPerformance: allFilters.overPerformance.length
  });

  return {
    ...allFilters,
    goodUnitsPercent:
      unitsWithRecentData.length > 0
        ? (allFilters.goodPerformance.length * 100) / unitsWithRecentData.length
        : 0
  };
};

export {
  dec2bin,
  dec2binReverse,
  filterTelemetryData,
  getAllFilteredData,
  getSummaryDataForDynamicTable,
  Metrics,
  searchUseMetric,
  useCombinedFuelHistory
};
